import React, { useMemo } from "react";

import {
  Card,
  CardBody,
  H6,
  Message,
  MessageVariants,
  P,
  PrimaryButton,
  ProminenceVariants,
  Small,
} from "@nel-ui/react";

import { ToggleSwitch } from "../../components";
import {
  DEBIT_CARD,
  INVERSTOR_TYPE_CODE,
  MESSAGE_HEADING_TYPE,
  MESSAGE_TYPE,
  VISA,
} from "../../constants";
import {
  accountSubTypeDetails,
  convertImpulseDelayInSec,
  getFormattedExpiry,
  getLastFourDigit,
  isFlexOneAccount,
} from "../../helpers";
import { IBlockDetailsCardProps } from "../../interfaces";

import "./style.css";

const mainClass = "block-details-card";
const BlockDetailsCard: React.FC<IBlockDetailsCardProps> = (
  props: IBlockDetailsCardProps
) => {
  const {
    cardNumber,
    expiryDate,
    impulseDelayRemaining = null,
    isDeclineAll = false,
    cardDescription,
    investorTypeCode,
    cardStaticContent,
    accountSubTypeCode,
    handleBlockStatusChange,
  } = props;

  const cardEnding = getLastFourDigit(cardNumber);
  const expDate = getFormattedExpiry(expiryDate);
  const impulseDelayInSec = convertImpulseDelayInSec(
    impulseDelayRemaining ?? "00:00:00"
  );
  const isWarnAndBlockOn = impulseDelayInSec > 0 && isDeclineAll;
  const { imageSrc, altText } = accountSubTypeDetails[accountSubTypeCode];

  const renderWarningMessage = () => (
    <Message
      className={`${mainClass}__warn-msg`}
      data-ref={MESSAGE_TYPE.WARNING}
      data-testid={`${mainClass}-warning-msg`}
      heading={MESSAGE_HEADING_TYPE.NOTE}
      headingId={MESSAGE_TYPE.WARNING}
      headingLevel={3}
      prominence={ProminenceVariants.HIGH}
      variant={MessageVariants.WARNING}
    >
      <P>{cardStaticContent.cardInfo.warning_msg_body}</P>
    </Message>
  );

  const renderImage = useMemo(() => {
    return (
      <div className={`${mainClass}__image`}>
        <img src={imageSrc} alt={`${altText}, ${VISA}, ${DEBIT_CARD}`} />
      </div>
    );
  }, [altText, imageSrc]);

  const renderCardEndingAndExpiry = () => (
    <>
      {`${cardStaticContent.cardInfo.card_ending} ${cardEnding} · ${cardStaticContent.cardInfo.expiry} ${expDate}`}
    </>
  );

  const renderCardDetails = useMemo(() => {
    return (
      <>
        <H6 as={"h3"} className={`${mainClass}__desc`}>
          {cardDescription} {DEBIT_CARD}
        </H6>
        <Small className={`${mainClass}__desc`}>
          {renderCardEndingAndExpiry()}
        </Small>
        {investorTypeCode === INVERSTOR_TYPE_CODE.JOINT_HOLDER && (
          <Small className={`${mainClass}__desc`}>
            {cardStaticContent.cardInfo.jont_account_info}
          </Small>
        )}
      </>
    );
    // eslint-disable-next-line
  }, [cardDescription]);

  const renderButton = () => (
    <PrimaryButton
      data-testid={`${mainClass}-block-on`}
      onClick={handleBlockStatusChange}
    >
      {cardStaticContent.cardInfo.leave_block_on}
    </PrimaryButton>
  );

  const renderToggleSwitch = () => (
    <ToggleSwitch
      aria-label={cardStaticContent.cardInfo.gambling_block}
      data-testid={`${mainClass}-toggle-switch`}
      label={cardStaticContent.cardInfo.gambling_block}
      onChange={handleBlockStatusChange}
      defaultToggle={isDeclineAll}
    />
  );

  return (
    <Card data-testid={mainClass} className={mainClass}>
      <CardBody>
        {isWarnAndBlockOn && renderWarningMessage()}
        <div className={`${mainClass}__container`}>
          {renderImage}
          <div className={`${mainClass}__details`}>
            {renderCardDetails}
            {!isFlexOneAccount(accountSubTypeCode) &&
              (isWarnAndBlockOn ? renderButton() : renderToggleSwitch())}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default BlockDetailsCard;
